import React, { useEffect } from 'react';
import { styled } from '@uhg-abyss/web/tools/styled';
import { V2Card } from '@uhg-abyss/web/ui/Card';
import { V2DateInput } from '@uhg-abyss/web/ui/DateInput';
import { V2SelectInput } from '@uhg-abyss/web/ui/SelectInput';
import { V2TextInput } from '@uhg-abyss/web/ui/TextInput';
import { V2RadioGroup } from '@uhg-abyss/web/ui/RadioGroup';
import { useFormV2 } from '@uhg-abyss/web/hooks/useForm';
import { dayjs } from '@uhg-abyss/web/tools/dayjs';
import { config } from '@uhg-abyss/web/tools/config';
import { useUser } from '@src/state/user/hooks';

const MAX_CLAIM_DATE = dayjs().endOf('day');
const MIN_CLAIM_DATE = dayjs().subtract(12, 'year').startOf('day');
const JOB_TITLE_OPTIONS = [
  { label: 'Administrative staff', value: 'administrativeStaff' },
  { label: 'Support staff', value: 'supportStaff' },
  { label: 'Clinician', value: 'clinician' },
  { label: 'Physician', value: 'physician' },
];

const SectionStack = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '20px',
});

const CardTitle = styled('h2', {
  margin: 0,
  color: '$primary1',
});

const HelperText = styled('p', {
  fontWeight: 600,
  margin: 0,
  fontSize: '16px',
  color: '$gray11',
});

const FormRows = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  width: '100%',
});

const ThreeColumnRow = styled('div', {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  gap: '16px',
  width: '100%',
});

const LastRow = styled('div', {
  maxWidth: '280px',
});

export const SubmitterInformation = () => {
  const form = useFormV2();
  const userInfo = useUser.state();
  const submittedClaim = form.watch('submitterInformation.submittedClaim');

  useEffect(() => {
    if (
      !form.getValues('submitterInformation.firstName') &&
      userInfo?.firstName
    ) {
      form.setValue('submitterInformation.firstName', userInfo.firstName);
    }

    if (
      !form.getValues('submitterInformation.lastName') &&
      userInfo?.lastName
    ) {
      form.setValue('submitterInformation.lastName', userInfo.lastName);
    }

    if (
      !form.getValues('submitterInformation.emailAddress') &&
      userInfo?.email
    ) {
      form.setValue('submitterInformation.emailAddress', userInfo.email);
    }

    if (
      !form.getValues('submitterInformation.phoneNumber') &&
      userInfo?.phone
    ) {
      const digitsOnly = userInfo.phone.replace(/\D/g, '');
      const tenDigitPhone = digitsOnly.slice(-10);
      form.setValue('submitterInformation.phoneNumber', tenDigitPhone);
    }
  }, []);

  useEffect(() => {
    if (submittedClaim !== 'Yes') {
      form.setValue('submitterInformation.claimDate', '', {
        touched: false,
        errors: { required: false },
      });
    }
  }, [submittedClaim]);

  return (
    <V2Card>
      <SectionStack>
        <CardTitle>Submitter information</CardTitle>
        <HelperText>
          If you need to change your name, email address or phone number, please
          visit the{' '}
          <a
            id="submitter-information-ohid-v2link"
            target="_blank"
            rel="noopener"
            href={`${config('ONE_HEALTHCARE_ID_URL')}/account`}
            hideIcon
          >
            One Healthcare ID page
          </a>{' '}
          to make updates.
        </HelperText>

        <FormRows>
          <ThreeColumnRow>
            <V2TextInput
              id="submitter-information-first-name"
              model="submitterInformation.firstName"
              label="First name"
              isDisabled
              maxLength={40}
            />

            <V2TextInput
              id="submitter-information-last-name"
              model="submitterInformation.lastName"
              label="Last name"
              isDisabled
              maxLength={40}
            />

            <V2SelectInput
              id="submitter-information-job-title"
              model="submitterInformation.jobTitle"
              label="Job title"
              options={JOB_TITLE_OPTIONS}
              validators={{ required: 'Job title is required.' }}
            />
          </ThreeColumnRow>

          <ThreeColumnRow>
            <V2TextInput
              id="submitter-information-email"
              model="submitterInformation.emailAddress"
              label="Email address"
              type="email"
              isDisabled
            />

            <V2TextInput
              id="submitter-information-phone"
              model="submitterInformation.phoneNumber"
              label="Phone number"
              mask="phone"
              isRequired
              formatter={(value) => String(value || '').replace(/\D/g, '')}
              validators={{
                required: 'Phone number is required.',
                validate: {
                  validPhone: (value) =>
                    !value ||
                    value.length === 10 ||
                    'Please enter a valid 10-digit phone number.',
                },
              }}
            />

            <V2RadioGroup
              id="submitter-information-contact-mode"
              label="Preferred mode of contact"
              model="submitterInformation.preferredModeOfContact"
              isRequired
              validators={{
                required: 'Preferred mode of contact is required.',
              }}
            >
              <V2RadioGroup.Radio
                id="submitter-information-contact-phone"
                value="Phone"
                label="Phone"
              />
              <V2RadioGroup.Radio
                id="submitter-information-contact-email"
                value="Email"
                label="Email"
              />
            </V2RadioGroup>
          </ThreeColumnRow>

          <LastRow>
            <V2RadioGroup
              id="submitter-information-submitted-claim"
              label="Have you submitted a claim?"
              model="submitterInformation.submittedClaim"
              isRequired
              validators={{ required: 'Please select Yes or No.' }}
            >
              <V2RadioGroup.Radio
                id="submitter-information-claim-yes"
                value="Yes"
                label="Yes"
              />
              <V2RadioGroup.Radio
                id="submitter-information-claim-no"
                value="No"
                label="No"
              />
            </V2RadioGroup>
            {submittedClaim === 'Yes' && (
              <V2DateInput
                id="submitter-information-claim-date"
                model="submitterInformation.claimDate"
                label="Claim submission date"
                openPosition="bottom"
                enableOutsideScroll
                maxDate={MAX_CLAIM_DATE}
                minDate={MIN_CLAIM_DATE}
                validators={{
                  required: 'Claim submission date is required.',
                  validate: {
                    validDate: (value) => {
                      const date = dayjs(value, 'MM/DD/YYYY', true);
                      if (!date.isValid()) {
                        return 'Please provide a valid date';
                      }
                      if (date.isAfter(dayjs(), 'day')) {
                        return 'Future dates are not allowed';
                      }
                      if (date.isBefore(MIN_CLAIM_DATE, 'day')) {
                        return 'Provide a valid date';
                      }
                      return true;
                    },
                  },
                }}
              />
            )}
          </LastRow>
        </FormRows>
      </SectionStack>
    </V2Card>
  );
};
